import { Clipper, FillRule, Path64, Paths64 } from 'clipper2-js';
import { footprintFor } from '@shared-lib/geometry/objectFootprint';

/**
 * Carving openings out of a wall face.
 *
 * The wall face is a POLYGON, not a rectangle — 18% of room-wall faces in the
 * offline corpus are not rectangular (mitred ends lean, stepped tops, split
 * levels). The previous approach clamped each opening to the axis-aligned
 * BOUNDING BOX of that polygon and handed the result to earcut as a hole. Two
 * things went wrong with that:
 *
 *   1. A hole can sit inside the bounding box while punching through the real
 *      boundary. earcut breaks on a hole that crosses the outline and emits
 *      stray triangles that fill the cutout back in — a doorway you cannot see
 *      through, with a diagonal edge across it.
 *   2. The along-wall width was read straight off `dimensions.x`, which is only
 *      the wall-parallel extent when the opening is parallel to its wall.
 *
 * Both are fixed here by construction rather than by tolerance: the opening
 * footprint is projected onto the wall's own axis, and the carve is a real
 * polygon boolean. There are no thresholds to tune — an opening that reaches
 * the wall's end becomes a notch, one that overhangs is clipped to the
 * boundary, and one that cuts clean through splits the face into two pieces.
 */

const SCALE = 1e6; // metres → integers for clipper

function toPath64(polygon) {
  const p = new Path64();
  for (const pt of polygon) {
    p.push({ x: Math.round(pt.x * SCALE), y: Math.round(pt.y * SCALE) });
  }
  return p;
}

const fromPath64 = (path) =>
  path.map((pt) => ({ x: Number(pt.x) / SCALE, y: Number(pt.y) / SCALE }));

/** Unsigned area of a simple polygon. */
export function polygonArea(polygon) {
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

function pointInPolygon(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x,
      yi = poly[i].y;
    const xj = poly[j].x,
      yj = poly[j].y;
    if (
      yi > pt.y !== yj > pt.y &&
      pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * The span an opening occupies ALONG its host wall.
 *
 * Uses the shared rotation-aware corner primitive rather than assuming the
 * opening's local X is parallel to the wall: the plan footprint is built at the
 * opening's own rotation and then projected onto the wall's axis. For a
 * parallel opening this returns exactly `dimensions.x`; for a rotated one it
 * returns the true projected footprint.
 *
 * @param {{center: {x,z}, dimensions: {x,z}, rotation?: {y}}} opening
 * @param {{x, z}} right  - the wall's unit axis in world X-Z
 * @param {{x, z}} origin - the wall-local origin, projected against
 * @returns {{min: number, max: number}} span in wall-local units
 */
export function alongWallSpan(opening, right, origin) {
  // The wall-convention footprint, NOT `calculateCorners`: the latter rotates
  // with the opposite z-sign to the wall convention `(cos ry, -sin ry)`, which
  // REFLECTS the rectangle. That is free on an axis-aligned wall — the
  // reflection maps the rectangle onto itself — and wrong by 2θ everywhere
  // else. Scans are axis-aligned so nothing showed it; once vertex edits
  // started producing angled walls routinely, a 0.98 m doorway on a 52.6° wall
  // carved 0.35 m.
  //
  // `footprintFor`, NOT its strict sibling `objectFootprint`: an OPENING CAN BE
  // PLANAR. RoomPlan measures a door or window as a surface, so `dimensions.z`
  // is 0 on 37% of dev's openings (7% of the offline corpus, which is a
  // curated subset — measure AFTER hydrating the slim room copies, or a
  // missing `dimensions` reads as a zero depth and the number comes out near
  // 100%). `objectFootprint` returns null for a non-positive depth by
  // contract. Routing through it (2026-08-31,
  // 97e3fc351) collapsed the footprint to the opening's CENTRE POINT, so the
  // span came back zero-width and `PolygonWalls` dropped the opening from the
  // carve — a doorway with no hole, and on a tiled wall, unbroken tile across
  // the door. `footprintFor` is documented for exactly this caller: a zero
  // depth yields a degenerate rectangle of the right WIDTH, never null.
  //
  // Zero depth is not an approximation here, it is the geometry. A planar
  // opening is a segment of length `dimensions.x` along its own axis, and its
  // projection onto the wall is w·cos θ — which is what this returns. Do not
  // "fix" a null by substituting a nominal depth: that invents a number and
  // carves WIDER than the opening (w·cos θ + d·sin θ) on any skewed opening.
  const corners = footprintFor(
    opening.center,
    opening.dimensions?.x,
    opening.dimensions?.z,
    opening.rotation?.y
  );
  // Only an unplaceable centre gets here, and `hasRenderableOpeningGeometry`
  // has already dropped those upstream. Report it as a zero span rather than a
  // second error channel: the carve site treats every zero-width outcome the
  // same way, and says so out loud.
  if (!corners) return { min: 0, max: 0 };
  const projected = corners.map(
    (c) => (c.x - origin.x) * right.x + (c.z - origin.z) * right.z
  );
  return { min: Math.min(...projected), max: Math.max(...projected) };
}

/**
 * Subtract opening footprints from a wall face polygon.
 *
 * @param {Array<{x, y}>} face - wall face in its local (along-wall, height) frame
 * @param {Array<Array<{x, y}>>} openings - footprints in that same frame
 * @returns {Array<{outline: Array<{x,y}>, holes: Array<Array<{x,y}>>}>}
 */
export function carveWallFace(face, openings) {
  if (!face || face.length < 3) return [];
  if (!openings || openings.length === 0) {
    return [{ outline: face, holes: [] }];
  }

  const subject = new Paths64();
  subject.push(toPath64(face));

  const clip = new Paths64();
  for (const opening of openings) {
    if (opening && opening.length >= 3) clip.push(toPath64(opening));
  }
  if (clip.length === 0) return [{ outline: face, holes: [] }];

  const result = Clipper.Difference(subject, clip, FillRule.NonZero) || [];

  // Clipper returns a flat ring list; orientation tells outlines from holes.
  // Group each hole under the outline that contains it.
  const outlines = [];
  const holes = [];
  for (const ring of result) {
    const poly = fromPath64(ring);
    if (poly.length < 3) continue;
    (Clipper.area(ring) >= 0 ? outlines : holes).push(poly);
  }

  const faces = outlines.map((outline) => ({ outline, holes: [] }));
  for (const hole of holes) {
    // Any vertex serves to locate the hole; use a vertex-average so a shared
    // edge doesn't make the containment test ambiguous.
    const probe = {
      x: hole.reduce((s, p) => s + p.x, 0) / hole.length,
      y: hole.reduce((s, p) => s + p.y, 0) / hole.length,
    };
    const owner =
      faces.find((f) => pointInPolygon(probe, f.outline)) ?? faces[0];
    if (owner) owner.holes.push(hole);
  }
  return faces;
}
